import {
  getLocaleCookie,
  setCookie,
} from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { publishEventParameter } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import * as CookieConsentManager from '@porterin/cookie-consent-manager/src/interface/CookieConsentManager.mjs';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import NextImageV2 from '../../../../ui/components/NextImageV2';
import { trLocales } from '../../../constants/LocaleConstants';
import { localeToString } from '../../../helpers/LocaleHelpers';
import {
  LanguageSelectorEntity,
  LanguageSelectorLocale,
} from '../../domain/entities/LanguageSelectorEntities';
import styles from '../styles/LanguageSelector.module.scss';

const GA_LANGUAGE_CHANGE_EVENT = 'fe_home_language_switch_click';
const NEXT_LOCALE_COOKIE_MAX_AGE = 30 * 60 * 60 * 24 * 7;

// Bridges the ReScript cookie-consent-manager lib until it is migrated to TS.
const acceptAllCookies = CookieConsentManager.acceptAllCookies as () => void;

function getNextLocaleCookieString(localeCode: string): string {
  return `NEXT_LOCALE=${localeCode}; maxage=${NEXT_LOCALE_COOKIE_MAX_AGE}; path=/`;
}

function triggerAcceptAllCookies(localeCode: string): void {
  if (trLocales.includes(localeCode)) {
    acceptAllCookies();
  }
}

interface LanguageSelectorCmpProps {
  content: LanguageSelectorEntity;
}

function LanguageSelectorCmp({
  content,
}: Readonly<LanguageSelectorCmpProps>): React.ReactElement {
  const router = useRouter();
  const { locale, asPath } = router;
  const { locales, language_icon, toggle_icon } = content;

  const [selectedLocale, setSelectedLocale] = useState<LanguageSelectorLocale>(
    locales[0],
  );

  const handleLocaleChange = (newLocale: LanguageSelectorLocale): void => {
    setSelectedLocale(newLocale);
    setCookie(getNextLocaleCookieString(newLocale.locale_code));
  };

  const onLocaleClick = (clickedLocale: LanguageSelectorLocale): void => {
    publishEventParameter(GA_LANGUAGE_CHANGE_EVENT, {
      selected_language: localeToString(clickedLocale.locale_code),
      language_code: clickedLocale.locale_code,
      previous_language: localeToString(selectedLocale.locale_code),
      previous_language_code: selectedLocale.locale_code,
    });
    triggerAcceptAllCookies(clickedLocale.locale_code);
    handleLocaleChange(clickedLocale);
  };

  const updateLocale = (
    localeOption: LanguageSelectorLocale | undefined,
  ): void => {
    handleLocaleChange(localeOption ?? locales[0]);
  };

  useEffect(() => {
    const nextLocaleCookie = getLocaleCookie();
    const localeFromCookie = locales.find(
      option => option.locale_code === nextLocaleCookie,
    );
    const currentLocale = locales.find(option => option.locale_code === locale);

    if (!localeFromCookie || nextLocaleCookie !== locale) {
      updateLocale(currentLocale);
    } else {
      setSelectedLocale(localeFromCookie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, locales]);

  return (
    <div className={styles['container']}>
      <div className={styles['label-container']}>
        <NextImageV2
          src={language_icon.path}
          alt={language_icon.alt_text}
          width={20}
          height={20}
        />
        <label className={styles['label']}>{selectedLocale.label}</label>
        <NextImageV2
          src={toggle_icon.path}
          alt={toggle_icon.alt_text}
          width={20}
          height={20}
        />
      </div>
      <div className={styles['language-list-container']}>
        <ul id="language-select" className={styles['language-list']}>
          {locales.map(option => {
            const isSelected =
              option.locale_code === selectedLocale.locale_code;
            return (
              <Link
                href={asPath}
                locale={option.locale_code}
                replace
                key={option.locale_code}
                onClick={(): void => onLocaleClick(option)}
              >
                <div
                  className={`${styles['language-item']} ${
                    isSelected ? styles['selected'] : ''
                  }`}
                >
                  <div className={styles['language-code']}>
                    {option.display_code}
                  </div>
                  <span>{option.label}</span>
                </div>
              </Link>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default LanguageSelectorCmp;
